import { filter, some } from "lodash/fp";

import useIntegration from "@app/analytics/hooks/integrations/useIntegration";
import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import useAddAccountProvider from "@app/analytics/hooks/sso/useAddAccountProvider";
import useConnectedProviders from "@app/analytics/hooks/sso/useConnectedProviders";
import useBanners from "@app/analytics/hooks/useBanners";
import type { ConnectedAccountProvider } from "@app/common/auth/api";
import useAuth from "@app/common/auth/useAuth";
import { SSO_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import type {
  Integration,
  IntegrationProviderId,
  SSOProviderId,
} from "@app/common/types";
import { pluralizePhrase } from "@app/common/utils";

import AlertBannerGroup, { AlertBannerGroupProps } from "@rui/AlertBannerGroup";

const getAlerts = ({
  integrationsNeedingReauth,
  providersNeedingReauth,
  zoomReauthRequired,
  isZoomConnected,
  onReauthSSO,
  onReauthIntegration,
}: {
  integrationsNeedingReauth: Integration[];
  providersNeedingReauth: ConnectedAccountProvider[];
  zoomReauthRequired: boolean;
  isZoomConnected: boolean;
  onReauthSSO: (providerId: SSOProviderId) => void;
  onReauthIntegration: (providerId: IntegrationProviderId) => void;
}): AlertBannerGroupProps["alerts"] => {
  const alerts: AlertBannerGroupProps["alerts"] = [];

  if (zoomReauthRequired) {
    if (isZoomConnected) {
      alerts.push({
        action: {
          label: "Reauthorize",
          onClick: () => onReauthSSO("zoom"),
        },
        title: "Reauthorize Zoom",
        content:
          "Read has detected one or more upcoming Zoom meetings on your calendar. Please reauthorize Zoom to ensure all reporting features work properly.",
      });
    } else {
      alerts.push({
        action: {
          label: "Connect",
          onClick: () => onReauthSSO("zoom"),
        },
        title: "Connect Zoom",
        content:
          "Read has detected one or more upcoming Zoom meetings on your calendar. Please connect a Zoom account to Read to ensure all reporting features work properly.",
      });
    }
  }

  providersNeedingReauth.forEach((provider) => {
    // don't show a second alert for zoom if we're already showing the more detailed zoom warning
    if (provider.providerId === "zoom" && zoomReauthRequired) return;
    alerts.push({
      action: {
        label: "Reauthorize",
        onClick: () => onReauthSSO(provider.providerId),
      },
      title: "Reauthorize",
      content: `${
        SSO_PROVIDERS_BY_ID[provider.providerId].displayName
      } requires reauthorization.`,
    });
  });

  integrationsNeedingReauth.forEach((integration) => {
    alerts.push({
      action: {
        label: "Reauthorize",
        onClick: () => onReauthIntegration(integration.providerId),
      },
      title: "Reauthorize",
      content: `${integration.displayName} requires reauthorization.`,
    });
  });

  return alerts;
};

const ReauthorizeProviderAlerts = ({ priority }: { priority: number }) => {
  const { user } = useAuth();
  const { userIntegrations } = useIntegrations();
  const { connectedAccountProviders } = useConnectedProviders();
  const { addAccountProvider } = useAddAccountProvider();
  const { authorize } = useIntegration();

  const providersNeedingReauth = filter(
    { reauthenticationRequired: true },
    connectedAccountProviders
  );
  const integrationsNeedingReauth = filter(
    { reauthRequired: true },
    userIntegrations
  );

  const zoomReauthRequired = some({ kind: "missing_zoom_sso" }, user?.alerts);

  const shouldShow = integrationsNeedingReauth.length > 0 || zoomReauthRequired;

  const { show } = useBanners({
    id: "reauthIntegrations",
    priority,
    shouldShow,
  });

  if (!show) return null;

  const isZoomConnected = some(
    { providerId: "zoom" },
    connectedAccountProviders
  );

  const handleAuthSSO = (providerId: SSOProviderId) => {
    addAccountProvider(providerId, {
      returnPath: window.location.pathname,
    });
  };

  const handleAuthIntegration = (providerId: IntegrationProviderId) => {
    authorize.mutate({
      providerId,
      returnRoute: window.location.pathname,
      scope: "user",
    });
  };

  const alerts = getAlerts({
    integrationsNeedingReauth,
    zoomReauthRequired,
    isZoomConnected,
    providersNeedingReauth,
    onReauthSSO: handleAuthSSO,
    onReauthIntegration: handleAuthIntegration,
  });

  return (
    <AlertBannerGroup
      alerts={alerts}
      severity="error"
      title={`${pluralizePhrase(alerts.length, "Item")} Require Attention`}
    />
  );
};

export default ReauthorizeProviderAlerts;
